/**
 * useSearchFeaturesAudit — small helper hook for the venue editor
 * "save & audit" flow.
 *
 * After the owner saves a venue setting, call `auditAfterSave(slug)`
 * to ping /api/sites/{slug}/search-features/owner-audit and surface
 * a toast for each newly-detected feature they haven't enabled yet.
 *
 * Click "Add" → navigate to the SearchFeaturesEditor page with that
 * code pre-selected (using ?focus=<code> hash).
 * Click "Skip" → dismiss; the prompt resurfaces on the next save.
 */
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function useSearchFeaturesAudit() {
  const navigate = useNavigate();

  const auditAfterSave = useCallback(async (slug) => {
    if (!slug) return;
    let resp;
    try {
      const { data } = await api.post(
        `/sites/${encodeURIComponent(slug)}/search-features/owner-audit`);
      resp = data;
    } catch {
      return;
    }
    if (!resp?.prompts?.length) return;
    // Limit to top 3 prompts to avoid stacking 12 toasts
    resp.prompts.slice(0, 3).forEach((p, i) => {
      setTimeout(() => {
        toast(p.label, {
          description: `${p.blurb}\nWhy: ${p.reason}`,
          duration: 9000,
          action: {
            label: "Add",
            onClick: () =>
              navigate(`/admin/sites/${encodeURIComponent(slug)}/search`
                       + `?focus=${encodeURIComponent(p.code)}`),
          },
        });
      }, i * 350);
    });
  }, [navigate]);

  return { auditAfterSave };
}
